import React, { useState } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import DataTable from "../DataTable";
import InlineUserDashboard from "./InlineUserDashboard";
import "../../index.css";

/* =======================
   GraphQL Query
======================= */

const GET_USERS_WITH_STATS = gql`
  query getUsersWithStats(
    $phone: String
    $minAmount: Int
    $minOrders: Int
    $startDate: String
    $endDate: String
    $minOrderValue: Int
    $maxAmount: Int
    $maxOrders: Int
  ) {
    getUsersWithStats(
      phone: $phone
      minAmount: $minAmount
      minOrders: $minOrders
      startDate: $startDate
      endDate: $endDate
      minOrderValue: $minOrderValue
      maxAmount: $maxAmount
      maxOrders: $maxOrders
    ) {
      users {
        id
        fullName
        contactNumber
        isBlocked
        totalOrders
        totalOrderAmount
      }
    }
  }
`;

/* =======================
   Component
======================= */

const CustomerManagement = () => {
  /* ---------- Filters ---------- */
  const [phone, setPhone] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [minOrders, setMinOrders] = useState("");
  const [customerType, setCustomerType] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [customOrders, setCustomOrders] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minOrderValue, setMinOrderValue] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [maxOrders, setMaxOrders] = useState("");

  /* ---------- Data ---------- */
  const [users, setUsers] = useState([]);

  /* ---------- Inline Dashboard ---------- */
  const [selectedUser, setSelectedUser] = useState(null);

  /* ---------- Fetch Users ---------- */
  const [fetchUsers, { loading }] = useLazyQuery(GET_USERS_WITH_STATS, {
    fetchPolicy: "network-only",
    onCompleted: (res) => {
      setUsers(res?.getUsersWithStats?.users || []);
    },
  });

  /* =======================
     Handlers
  ======================= */

  const handleApplyFilters = () => {
    if (phone && phone.length !== 10) return;

    fetchUsers({
      variables: {
        phone: phone || null,

        minAmount: customAmount
          ? Number(customAmount)
          : minAmount
            ? Number(minAmount)
            : null,

        minOrders: customOrders
          ? Number(customOrders)
          : minOrders
            ? Number(minOrders)
            : null,

        maxAmount: maxAmount ? Number(maxAmount) : null,
        maxOrders: maxOrders ? Number(maxOrders) : null,

        startDate: startDate || null,
        endDate: endDate || null,
        minOrderValue: minOrderValue ? Number(minOrderValue) : null,
      },
    });
  };

  const handleReset = () => {
    setPhone("");
    setMinAmount("");
    setMinOrders("");
    setMaxAmount("");
    setMaxOrders("");
    setCustomAmount("");
    setCustomOrders("");
    setStartDate("");
    setEndDate("");
    setMinOrderValue("");
    setUsers([]);
  };

  /* =======================
     INLINE DASHBOARD MODE
  ======================= */

  if (selectedUser) {
    return (
      <InlineUserDashboard
        user={selectedUser}
        onBack={() => setSelectedUser(null)}
      />
    );
  }

  /* =======================
     LIST MODE
  ======================= */

  return (
    <div className="p-4 space-y-6">
      {/* ================= FILTER BAR ================= */}
      <div className="bg-[#1f1f1f] p-4 rounded-lg">
        <div className="flex flex-wrap items-center gap-3">
          {/* Phone */}
          <input
            type="text"
            inputMode="numeric"
            placeholder="Phone"
            value={phone}
            onChange={(e) => {
              // No maxLength: it truncates pasted "81075 29992" before spaces are stripped
              let digits = e.target.value.replace(/\D/g, "");
              if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
              else if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
              setPhone(digits.slice(0, 10));
            }}
            className="p-2 rounded-md w-36"
          />

          {/* Start Date */}
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="p-2 rounded-md"
          />

          {/* End Date */}
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="p-2 rounded-md"
          />

          {/* Individual Order Value */}
          <input
            type="number"
            placeholder="Min Order Value"
            value={minOrderValue}
            onChange={(e) => setMinOrderValue(e.target.value)}
            className="p-2 rounded-md w-40"
          />

          {/* Total Order Amount */}
          <select
            value={minAmount}
            onChange={(e) => {
              setMinAmount(e.target.value);
              setCustomAmount("");
            }}
            className="p-2 rounded-md w-40"
          >
            <option value="">Total Amount</option>
            <option value="500">₹500+</option>
            <option value="1000">₹1000+</option>
            <option value="5000">₹5000+</option>
            <option value="custom">Custom</option>
          </select>

          <select
            value={maxAmount}
            onChange={(e) => {
              setMaxAmount(e.target.value);
            }}
            className="p-2 rounded-md w-40"
          >
            <option value="">Max Amount</option>
            <option value="1000">₹1000</option>
            <option value="2000">₹2000</option>
          </select>

          {minAmount === "custom" && (
            <input
              type="number"
              placeholder="Amount"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              className="p-2 rounded-md w-32"
            />
          )}

          {/* Total Orders */}
          <select
            value={minOrders}
            onChange={(e) => {
              setMinOrders(e.target.value);
              setCustomOrders("");
            }}
            className="p-2 rounded-md w-32"
          >
            <option value="">Orders</option>
            <option value="1">1+</option>
            <option value="5">5+</option>
            <option value="10">10+</option>
            <option value="custom">Custom</option>
          </select>

          {minOrders === "custom" && (
            <input
              type="number"
              placeholder="Count"
              value={customOrders}
              onChange={(e) => setCustomOrders(e.target.value)}
              className="p-2 rounded-md w-28"
            />
          )}

          <select
            value={maxOrders}
            onChange={(e) => {
              setMaxOrders(e.target.value);
            }}
            className="p-2 rounded-md w-32"
          >
            <option value="">Max Orders</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="5">5</option>
          </select>

          {/* Buttons */}
          <button
            onClick={handleApplyFilters}
            className="bg-white text-black px-4 py-2 rounded-md font-medium"
          >
            Apply
          </button>

          <button
            onClick={handleReset}
            className="bg-gray-300 text-black px-4 py-2 rounded-md font-medium"
          >
            Reset
          </button>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      {loading && <p className="text-white">Loading users...</p>}

      <div className="hidden md:block">
      <DataTable tableName="User-management" key={Date.now()}>
        <thead>
          <tr>
            <th className="text-white">Name</th>
            <th className="text-white">Mobile</th>
            <th className="text-white">Orders</th>
            <th className="text-white">Amount</th>
            <th className="text-white">Action</th>
          </tr>
        </thead>

        <tbody>
          {users.map((user) => (
            <tr key={user.id} className="border-b border-[#E0E0E0]">
              <td className="text-white px-2">
                {user.fullName}
                {user.totalOrderAmount >= 10000 && (
                  <span className="ml-2 text-xs bg-yellow-400 text-black px-2 py-0.5 rounded">
                    VIP ⭐
                  </span>
                )}
                {user.isBlocked && (
                  <span className="ml-2 text-xs bg-red-600 text-white px-2 py-0.5 rounded">
                    Blocked
                  </span>
                )}
              </td>

              <td className="text-white text-center">{user.contactNumber}</td>

              <td className="text-white text-center">{user.totalOrders}</td>

              <td className="text-white text-center">
                ₹{user.totalOrderAmount}
              </td>

              <td className="text-center">
                <button
                  className="rounded-md bg-white py-2 px-3 text-black text-sm font-medium"
                  onClick={() => setSelectedUser(user)}
                >
                  Dashboard
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      </div>

      {/* Mobile: card view */}
      <div className="md:hidden flex flex-col gap-3">
        {users.map((user) => (
          <div
            key={user.id}
            className="relative bg-[#0c0b0b] border border-[#2a2a2a] rounded-xl p-4 shadow"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-white font-semibold text-sm break-words flex items-center gap-2 flex-wrap">
                {user.fullName}
                {user.totalOrderAmount >= 10000 && (
                  <span className="text-xs bg-yellow-400 text-black px-2 py-0.5 rounded">
                    VIP ⭐
                  </span>
                )}
                {user.isBlocked && (
                  <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded">
                    Blocked
                  </span>
                )}
              </span>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              <div>
                <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                  Mobile
                </div>
                <div className="text-white">{user.contactNumber}</div>
              </div>
              <div>
                <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                  Orders
                </div>
                <div className="text-white">{user.totalOrders}</div>
              </div>
              <div>
                <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                  Amount
                </div>
                <div className="text-white">₹{user.totalOrderAmount}</div>
              </div>
            </div>

            <div className="flex gap-2 mt-3 pt-3 border-t border-[#2a2a2a]">
              <button
                className="flex-1 rounded-md bg-white py-1.5 px-3 text-black text-sm font-medium"
                onClick={() => setSelectedUser(user)}
              >
                Dashboard
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerManagement;
